import React, { createContext, useMemo } from 'react';
import { noop } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { trackMixpanelCardInteraction, trackMixpanelCardView } from '../../../../../utils/events/card';
import { Platform } from '../../../../../utils/events/types';
import { Action } from '../../../../../utils/events/card/types';
import { logError } from '../../../../../utils/logger/logError';

import { getTranslatedSubCategoryName } from '../../../../../utils/catalog/getTranslatedSubCategoryName';
import { getTranslatedNestedSubCategoryName } from '../../../../../utils/catalog/getTranslatedNestedSubCategoryName';
import { DEFAULT_MIXPANEL_ENABLED } from './constants';
import { MixpanelContextProviderProps, TMixpanelContext } from './types';

const initialContext: TMixpanelContext = {
  trackCardInteraction: noop,
  trackCardView: noop,
};

export const MixpanelContext = createContext<TMixpanelContext>(initialContext);

export const MixpanelContextProvider = ({
  inspireDeliveries,
  children,
  componentType,
  mixpanelEnabled = DEFAULT_MIXPANEL_ENABLED,
  mixpanelOptions,
}: MixpanelContextProviderProps) => {
  if (mixpanelEnabled && !mixpanelOptions) {
    throw new Error('When [mixpanelEnabled=true], "mixpanelOptions" must be provided');
  }

  const { pageName = '', hostingScreen = '', component = '', enrichment = {} } = mixpanelOptions || {};

  const context = useMemo<TMixpanelContext>(() => {
    if (!mixpanelEnabled) {
      return initialContext;
    }

    const { isMobile } = getContext();

    const trackCardInteraction = (id: string, action: Action) => {
      const index = inspireDeliveries.findIndex((delivery) => delivery.id === id);

      if (index < 0) {
        logError('MixpanelContext', `trackCardInteraction: Cannot find delivery with ID "${id}"`);
        return;
      }

      const inspireDelivery = inspireDeliveries[index];

      const subCategoryId = inspireDelivery.category.subCategoryId;
      const nestedSubCategoryId = inspireDelivery.category.nestedCategoryId;

      trackMixpanelCardInteraction({
        positionInFeed: index + 1,
        hostingScreen,
        pageName,
        deliveryId: id,
        gigId: inspireDelivery.gig.id,
        sellerId: inspireDelivery.seller.id,
        itemType: inspireDelivery.originalDeliveryAttachment.type,
        subCategoryId,
        nestedSubCategoryId,
        subCategoryName: getTranslatedSubCategoryName(subCategoryId),
        nestedSubCategoryName: nestedSubCategoryId
          ? getTranslatedNestedSubCategoryName(subCategoryId, nestedSubCategoryId)
          : '',
        price: inspireDelivery.gig.startingPackagePrice?.amount,
        width: inspireDelivery.originalDeliveryAttachment.metadata.width,
        height: inspireDelivery.originalDeliveryAttachment.metadata.height,
        action,
        platform: isMobile ? Platform.Mobile : Platform.Desktop,
        component,
        enrichment,
      });
    };

    const trackCardView = (id: string) => {
      const index = inspireDeliveries.findIndex((delivery) => delivery.id === id);

      if (index < 0) {
        logError('MixpanelContext', `trackCardView: Cannot find delivery with ID "${id}"`);
        return;
      }

      const inspireDelivery = inspireDeliveries[index];

      const subCategoryId = inspireDelivery.category.subCategoryId;
      const nestedSubCategoryId = inspireDelivery.category.nestedCategoryId;

      trackMixpanelCardView({
        positionInFeed: index + 1,
        pageName,
        deliveryId: id,
        gigId: inspireDelivery.gig.id,
        sellerId: inspireDelivery.seller.id,
        itemType: inspireDelivery.originalDeliveryAttachment.type,
        subCategoryId,
        nestedSubCategoryId,
        subCategoryName: getTranslatedSubCategoryName(subCategoryId),
        nestedSubCategoryName: nestedSubCategoryId
          ? getTranslatedNestedSubCategoryName(subCategoryId, nestedSubCategoryId)
          : '',
        price: inspireDelivery.gig.startingPackagePrice?.amount,
        width: inspireDelivery.originalDeliveryAttachment.metadata.width,
        height: inspireDelivery.originalDeliveryAttachment.metadata.height,
        platform: isMobile ? Platform.Mobile : Platform.Desktop,
        component,
        componentType,
        enrichment,
      });
    };

    return {
      trackCardInteraction,
      trackCardView,
    };
  }, [inspireDeliveries, componentType, mixpanelEnabled, pageName, hostingScreen, component, enrichment]);

  return <MixpanelContext.Provider value={context}>{children}</MixpanelContext.Provider>;
};
